/*
 * **⌥↑ / ⌥↓ in der Liste** (#204, Unter-Plan Tastatur): die ausgewählten Aufgaben um eine
 * Stelle. Jede rückt für sich, Lücken zwischen ihnen bleiben — wie beim Zeilen-Verschieben im
 * Editor. Stößt die erste (bzw. letzte) an den Rand, bewegt sich nichts.
 *
 * Ein Abschnittskopf ist eine Stelle: ⌥↓ an der letzten Aufgabe eines Abschnitts setzt sie
 * oben in den nächsten (wie beim Ziehen). Wo die Abschnitte nur gedacht sind (Kurz notiert),
 * ist die Grenze ein Rand.
 *
 * Liegt in `lib/`, damit die Mobile-App dieselbe Regel teilt (ADR 0036).
 */

export interface OrderSegment {
  section: { id: string; order: number } | null
  tasks: ReadonlyArray<{ id: string; order: number; sectionId?: string | null }>
}

export interface OrderUpdate { id: string; order: number; sectionId?: string | null }

export function moveBySteps(
  segments: readonly OrderSegment[],
  selected: ReadonlySet<string>,
  direction: -1 | 1,
  { crossSections = true }: { crossSections?: boolean } = {},
): OrderUpdate[] | null {
  const lanes = stepLanes(segments.map(seg => seg.tasks.map(t => t.id)), selected, direction, {
    // In einen Abschnitt ohne Kopf (den losen ganz oben) führt nur der Weg nach oben.
    crossSections,
    canEnter: (from, to) => (to > from ? segments[to]!.section !== null : segments[from]!.section !== null),
  })
  if (!lanes) return null
  // Gedachte Abschnitte (Kurz notiert: nach Alter, `order` 0, keine Dexie-Zeile) wie beim Ziehen:
  // Ihre Kennung gehört nicht in die Aufgabe, ihre `order` ist keine Grenze — sonst zählte ein
  // Schritt die Gruppe auf 0 durch (#304).
  return ordersFor(crossSections ? segments : segments.map(seg => ({ ...seg, section: null })), lanes, selected)
}

/**
 * Die Reihenfolge nach dem Schritt — Kennungen je Abschnitt (`lanes`); `null`, wenn die
 * Auswahl an einen Rand stößt. Auch für die Checkliste (ein einziger Abschnitt).
 */
export function stepLanes(
  source: readonly (readonly string[])[],
  selected: ReadonlySet<string>,
  direction: -1 | 1,
  { crossSections = false, canEnter = () => true }: { crossSections?: boolean; canEnter?: (from: number, to: number) => boolean } = {},
): string[][] | null {
  const lanes = source.map(lane => [...lane])
  const picked = lanes.flat().filter(id => selected.has(id))
  if (picked.length === 0) return null
  // Aufwärts von oben nach unten abarbeiten, abwärts von unten nach oben: So rückt ein Block
  // geschlossen, und jede Zeile findet ihren Platz frei vor.
  for (const id of direction === -1 ? picked : [...picked].reverse()) {
    const s = lanes.findIndex(lane => lane.includes(id))
    const lane = lanes[s]!
    const i = lane.indexOf(id)
    const j = i + direction
    if (j >= 0 && j < lane.length) {
      if (selected.has(lane[j]!)) return null // der Block stößt an den Rand
      lane.splice(i, 1)
      lane.splice(j, 0, id)
      continue
    }
    // An der Abschnittsgrenze: in den Nachbarabschnitt, über den Kopf hinweg.
    const next = s + direction
    if (!crossSections || next < 0 || next >= lanes.length || !canEnter(s, next)) return null
    lane.splice(i, 1)
    if (direction === 1) lanes[next]!.unshift(id)
    else lanes[next]!.push(id)
  }
  return lanes
}

/**
 * Neue `order` für die bewegten Zeilen: die Mitte zwischen ihren Nachbarn; ohne Platz wird der Abschnitt neu durchgezählt.
 * Auch fürs Ziehen am Handy: `lanes` ist die Reihenfolge nach dem Ablegen.
 */
export function ordersFor(segments: readonly OrderSegment[], lanes: string[][], moved: ReadonlySet<string>): OrderUpdate[] {
  const byId = new Map(segments.flatMap(seg => seg.tasks.map(t => [t.id, t] as const)))
  const updates: OrderUpdate[] = []

  lanes.forEach((lane, s) => {
    const seg = segments[s]!
    const sectionId = seg.section?.id ?? null
    const floor = seg.section?.order
    const ceiling = segments[s + 1]?.section?.order
    const known = (id: string) => (moved.has(id) ? undefined : byId.get(id)!.order)
    const changesSection = (id: string) => (byId.get(id)!.sectionId ?? null) !== sectionId
    const laneStart = updates.length

    let k = 0
    while (k < lane.length) {
      if (!moved.has(lane[k]!)) { k++; continue }
      const start = k
      while (k < lane.length && moved.has(lane[k]!)) k++
      const run = lane.slice(start, k)
      const before = start > 0 ? known(lane[start - 1]!) : undefined
      const after = k < lane.length ? known(lane[k]!) : undefined
      // Ohne Nachbarn und Kopf darüber (leerer Block ohne Abschnitt) unter dem nächsten Kopf bleiben — steht der bei 0
      // oder darunter, landete die Zeile sonst auf seiner `order`.
      const lower = before ?? floor ?? (after !== undefined ? after - run.length - 1 : ceiling !== undefined ? ceiling - run.length - 1 : 0)
      const upper = after ?? ceiling ?? lower + run.length + 1
      const step = (upper - lower) / (run.length + 1)
      if (!(step > 0)) {
        // Kein Platz: den ganzen Abschnitt neu durchzählen — statt der Werte, die schon stehen.
        updates.splice(laneStart, updates.length - laneStart, ...renumber(lane, floor, ceiling, sectionId, changesSection))
        return
      }
      run.forEach((id, n) => updates.push({ id, order: lower + step * (n + 1), ...(changesSection(id) ? { sectionId } : {}) }))
    }
  })
  return updates
}

function renumber(lane: string[], floor: number | undefined, ceiling: number | undefined, sectionId: string | null, changesSection: (id: string) => boolean): OrderUpdate[] {
  const base = floor ?? 0
  const gap = ((ceiling ?? base + lane.length + 1) - base) / (lane.length + 1)
  return lane.map((id, i) => ({ id, order: base + gap * (i + 1), ...(changesSection(id) ? { sectionId } : {}) }))
}
